import { defaultTotalCapacity } from "@woven/contracts";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useAuth } from "../../auth/context";
import { useControlApiClient } from "../../lib/hooks";
import { AccountCapacityContext, type CapacityState } from "./AccountCapacityContext";

const guestCapacity: CapacityState = {
  status: "ready",
  data: {
    totalCapacity: defaultTotalCapacity("guest"),
    allocatedCapacity: 0,
    availableCapacity: 0,
  },
};

export function AccountCapacityProvider({ children }: { children: ReactNode }) {
  const client = useControlApiClient();
  const { user } = useAuth();
  const uid = user?.uid;
  const { pathname, search, key } = useLocation();
  const [revision, setRevision] = useState(0);
  const scope = useMemo(
    () => ({ client, uid, revision, pathname, search, key }),
    [client, uid, revision, pathname, search, key]
  );
  const [snapshot, setSnapshot] = useState<{ scope: typeof scope; state: CapacityState } | null>(
    null
  );
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    if (!scope.uid) return;
    const controller = new AbortController();
    scope.client.getAccountCapacity(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setSnapshot({ scope, state: { status: "ready", data } });
      },
      () => {
        if (!controller.signal.aborted) setSnapshot({ scope, state: { status: "error" } });
      }
    );
    return () => controller.abort();
  }, [scope]);

  const state: CapacityState = !uid
    ? guestCapacity
    : snapshot?.scope === scope
      ? snapshot.state
      : { status: "loading" };
  return (
    <AccountCapacityContext.Provider value={{ state, refresh }}>
      {children}
    </AccountCapacityContext.Provider>
  );
}
